<template>
  <v-app>
    <!-- ===================================================== -->
    <!-- TOP SYSTEM BAR -->
    <!-- ===================================================== -->
    <v-app-bar
      flat
      :style="{
        background: themeStore.themeStyles.background,
        color: themeStore.themeStyles.color
      }"
      style="padding-left: 30px; padding-right: 30px;"
    >
      <!-- SIDEBAR CONTROLS -->
      <div class="d-flex align-center">
        <v-btn
          icon
          size="small"
          variant="text"
          @click="toggleSidebar"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>

        <v-btn
          icon
          size="small"
          variant="text"
          @click="togglePin"
        >
          <v-icon :color="isPinned ? 'primary' : ''">
            mdi-pin
          </v-icon>
        </v-btn>
      </div>

      <!-- LOGO -->
      <router-link :to="{ name: 'PublicHome' }">
        <v-img
          v-if="getCurrentTheme === 'light'"
          :src="getLogoLight"
          style="width: 200px; height: 42px;"
        />

        <v-img
          v-else
          :src="getLogoDark"
          style="width: 200px; height: 42px;"
        />
      </router-link>

      <v-divider vertical class="mx-1" />

      <!-- MEGA MENUS -->
      <div class="d-flex align-center">
        <MegaMenuSetup />
        <MegaMenu />
        <MegaMenuResources />
      </div>

      <v-spacer />

      <v-divider vertical class="mx-3" />

      <!-- SETTINGS -->
      <v-menu
        v-model="settingsMenu"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <span
            v-bind="props"
            v-ripple
            class="icon-btn d-inline-flex align-center justify-center"
          >
            <v-icon size="large">
              mdi-theme-light-dark
            </v-icon>
          </span>
        </template>

        <v-card min-width="300">
          <v-list>
            <v-list-item
              prepend-avatar="https://cdn.vuetifyjs.com/images/john.jpg"
              subtitle="Founder of Vuetify"
              title="John Leider"
            >
              <template #append>
                <v-btn
                  icon="mdi-logout-variant"
                  variant="text"
                  @click="handleLogout"
                />
              </template>
            </v-list-item>
          </v-list>

          <v-divider />

          <v-row class="ma-2">
            <v-col cols="4" class="text-center">
              <v-switch
                v-model="lightSwitch"
                color="purple"
                hide-details
                @change="selectTheme('light')"
              />
              <div>Light</div>
            </v-col>

            <v-col cols="4" class="text-center">
              <v-switch
                v-model="darkSwitch"
                color="purple"
                hide-details
                @change="selectTheme('dark')"
              />
              <div>Dark</div>
            </v-col>

            <v-col cols="4" class="text-center">
              <v-switch
                v-model="hybridSwitch"
                color="purple"
                hide-details
                @change="selectTheme('hybrid')"
              />
              <div>Hybrid</div>
            </v-col>
          </v-row>

          <v-divider />

          <v-list>
            <v-list-item>
              <v-switch
                v-model="message"
                color="purple"
                label="Enable messages"
                hide-details
              />
            </v-list-item>

            <v-list-item>
              <v-switch
                v-model="hints"
                color="purple"
                label="Enable hints"
                hide-details
              />
            </v-list-item>
          </v-list>

          <v-card-actions>
            <v-spacer />

            <v-btn
              variant="text"
              @click="settingsMenu = false"
            >
              Cancel
            </v-btn>

            <v-btn
              color="primary"
              variant="text"
              @click="settingsMenu = false"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>

      <!-- LANGUAGE -->
      <v-menu
        v-model="langMenu"
        offset-y
      >
        <template #activator="{ props }">
          <span
            v-bind="props"
            class="formatlang"
            style="
              display: inline-flex;
              align-items: center;
              cursor: pointer;
              gap: 2px;
              margin-left: 10px;
            "
          >
            <LanguageSwitcher />
          </span>
        </template>
      </v-menu>
    </v-app-bar>

    <!-- ===================================================== -->
    <!-- MAIN APPLICATION -->
    <!-- ===================================================== -->
    <v-layout class="procurement-layout">

      <!-- PROCUREMENT SIDEBAR -->
      <aside
        v-show="sidebarOpen"
        class="procurement-sidebar-container"
        :style="{
          width: `${sidebarWidth}px`,
          minWidth: `${sidebarWidth}px`
        }"
      >
        <ProcurementSidebar
          @resize="handleSidebarResize"
          @panel-state="handleSidebarResize"
        />
      </aside>

      <!-- MAIN CONTENT -->
      <v-main
        class="procurement-main"
        :style="{
          minWidth: 0
        }"
      >
        <router-view />

        <ProcurementFooter />
      </v-main>

    </v-layout>
  </v-app>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted
} from "vue"

import {
  useThemeStore,
  useAuthStore
} from "@/store/auth"

import { useRouter } from "vue-router"
import { useLangStore } from "@/store/lang"
import { useTenantStore } from "@/store/tenantStore.js"

import ProcurementSidebar from "../components/ProcurementSidebar.vue"
import ProcurementFooter from "@/modules/procurement/components/ProcurementFooter.vue"

import LanguageSwitcher from "@/components/LanguageSwitcher.vue"
import MegaMenu from "@/components/MegaMenu.vue"
import MegaMenuSetup from "@/components/MegaMenuSetup.vue"
import MegaMenuResources from "@/components/MegaMenuResources.vue"

/* =========================================================
   STORES
========================================================= */

const themeStore = useThemeStore()
const auth = useAuthStore()
const router = useRouter()

const tenantStore = useTenantStore()
const langStore = useLangStore()

const lang = computed(
  () => langStore.currentLanguage || "en"
)

/* =========================================================
   SIDEBAR
========================================================= */

const sidebarOpen = ref(true)
const isPinned = ref(false)

/*
  ProcurementSidebar defaults:

  icon rail = 64px
  panel     = 280px

  total     = 344px
*/
const sidebarWidth = ref(344)

function handleSidebarResize(detail) {
  if (!detail) {
    return
  }

  sidebarWidth.value =
    Number(detail.totalWidth || 64)
}

function toggleSidebar() {
  sidebarOpen.value =
    !sidebarOpen.value
}

function togglePin() {
  isPinned.value =
    !isPinned.value

  if (isPinned.value) {
    sidebarOpen.value = true
  }
}

/* =========================================================
   SETTINGS
========================================================= */

const settingsMenu = ref(false)
const langMenu = ref(false)

const message = ref(false)
const hints = ref(false)

const lightSwitch = ref(false)
const darkSwitch = ref(false)
const hybridSwitch = ref(false)

function selectTheme(theme) {
  lightSwitch.value =
    theme === "light"

  darkSwitch.value =
    theme === "dark"

  hybridSwitch.value =
    theme === "hybrid"

  themeStore.setTheme(theme)
}

/* =========================================================
   AUTH
========================================================= */

function handleLogout() {
  auth.logout()
}

/* =========================================================
   TENANT LOGO
========================================================= */

const getLogoLight = ref("")
const getLogoDark = ref("")
const getCurrentTheme = ref("")

onMounted(async () => {
  await tenantStore.loadTenant()

  getLogoLight.value =
    localStorage.getItem("LogoLight") || ""

  getLogoDark.value =
    localStorage.getItem("LogoDark") || ""

  getCurrentTheme.value =
    localStorage.getItem("currentTheme") || "light"
})
</script>

<style scoped>
.procurement-layout {
  margin-top: 64px;
  min-height: calc(100vh - 64px);
  width: 100%;
  display: flex;
  align-items: stretch;
}

.procurement-sidebar-container {
  position: relative;
  flex: 0 0 auto;
  min-height: calc(100vh - 64px);
  overflow: visible;
  transition:
    width 160ms ease,
    min-width 160ms ease;
  z-index: 10;
}

.procurement-main {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.icon-btn:hover {
  background-color: rgba(0, 0, 0, 0.08);
}

@media (max-width: 959px) {
  .procurement-sidebar-container {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    z-index: 100;
  }
}
</style>